import validator from "validator";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { get, isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import images from "../../constant/images";
import { redirectToPath } from "../../helper/com-helper";
import { getPath } from "../../helper/commonHelper";
import useRecaptchaV3 from "../../hooks/recaptchaV3";
import { loaderStateChange } from "../../store/Slices/loading";
import { recaptchaSiteKey } from "../../utils/ApiHandler";
import { useLoginHandlers } from "./useLoginHandlers";
import "./LoginForm.scss";
import Glass from "../../components/glassEffect";
import { focusVisibleSx, onEnterSpace } from "@3sc/common-component";
import { readAppValue } from "../../config/runtimeAccessor";

function LoginForm() {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [showValidityModal, setShowValidityModal] = useState(false);
  const [validityMessage, setValidityMessage] = useState("");
  const [path] = useState(globalThis.location.href);
  const user = readAppValue("user");
  const { t } = useTranslation();
  const executeRecaptcha = useRecaptchaV3(recaptchaSiteKey);

  const textInputProps = {
    disableUnderline: true,
    sx: {
      "& input": {
        padding: "1.5rem 2rem",
        color: "white",
      },
    },
  };

  const {
    submitUser,
    forgotPasswordSubmit,
    handleSSO,
    emailError,
    setEmailError,
    onOKTAButtonClick,
    onEffectFn,
    handleKeypress,
  } = useLoginHandlers({
    dispatch,
    navigate,
    t,
    executeRecaptcha,
    setValidityMessage,
    setShowValidityModal,
  });

  useEffect(() => {
    if (!isEmpty(user)) {
      const permissions = get(user, "permissions", []);
      const roles = get(user, "roles", []);
      const path = getPath(permissions, roles);
      redirectToPath(path);
    }
  }, [user]);

  useEffect(() => {
    onEffectFn(path);
  }, [path]);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    showPassword: false,
  });

  const { email, password, showPassword } = formData;

  const handleClickShowPassword = () => {
    setFormData({
      ...formData,
      showPassword: !formData.showPassword,
    });
  };

  const handleChange = (prop) => (event) => {
    const value =
      prop === "email" ? event.target.value?.toLowerCase() : event.target.value;

    if (prop === "email") {
      if (value && !validator.isEmail(value)) {
        setEmailError("emailAddressValidation");
      } else {
        setEmailError("");
      }
    }

    setFormData({
      ...formData,
      [prop]: value,
    });
  };

  const emailAddressTextFieldStyling = (props) => ({
    fontSize: "1.3rem",
    pointerEvents: "auto",
    width: props?.width || "20rem !important",

    /* Text color (main input color) */
    "& .MuiInputBase-input": {
      color: theme.palette.primary.tooltipBackground,
      fontSize: "1.2rem",
    },

    /* Root color (placeholder or inherited) */
    "& .MuiInputBase-root": {
      width: props?.width || "20rem !important",
      color: theme.palette.primary.tooltipBackground,
    },

    /* Autofill styling */
    "& input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
      WebkitTextFillColor: `${theme.palette.primary.tooltipBackground}!important`,
      transition: "background-color 9999s ease-in-out 0s",
      fontSize: "1.2rem !important",
    },
    "& input:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
      WebkitTextFillColor: `${theme.palette.primary.tooltipBackground}!important`,
      fontSize: "1.2rem !important",
    },

  });

  const isDisabled = !email || !password || Boolean(emailError);

  return (
    <Stack direction={"column"}
      gap={"2rem"}
      alignItems={"flex-end"}
      sx={{
        height: "100%",
        zIndex: 2,
        flex: 1,
      }}>
      <Stack
        direction={"column"}
        gap={"2rem"}
        alignItems={"flex-end"}
        justifyContent={"center"}
        sx={{
          flex: 1,
        }}
      >
        <Stack
          justifyContent={"center"}
          alignItems={"flex-end"}
          sx={{
            mb: "4rem",
          }}
        >
          <Typography
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              fontSize: "4.2rem",
              color: theme.palette.primary.tooltipBackground,
              width: "76rem",
              lineHeight: "1.10 !important",
              marginBottom: "2rem"
            }}
          >
            {t("HEADER1")}
          </Typography>

          <Box sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            width: "76rem"
          }}>
            <Typography
              sx={{
                fontSize: "1.3rem",
                color: theme.palette.brandColor.grey2,
              }}
            >
              {t("HEADER2")}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={"column"}>
          <Stack flexDirection={"row"} gap={"1.5rem"}>
            <Glass
              className="my-gloss"
              sx={{ width: "35rem", borderRadius: "2rem", border: "none" }}
            >
              <TextField
                variant="standard"
                placeholder={t("enterEmailAddressPlaceholder")}
                value={email}
                onChange={handleChange("email")}
                InputProps={textInputProps}
                data-testid="email-input"
                sx={emailAddressTextFieldStyling({ width: "40rem !important" })}
              />

            </Glass>

            <Glass
              style={{
                borderRadius: "2rem",
                width: "25rem!important",
              }}
            >
              <Stack direction={"row"} justifyContent={"space-between"}>
                <TextField
                  variant="standard"
                  placeholder={t("passwordPlaceholder")}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  data-testid="password-input"
                  onKeyDown={(e) => {
                    handleKeypress(e, email, password);
                  }}
                  value={password}
                  onChange={handleChange("password")}
                  InputProps={textInputProps}
                  sx={emailAddressTextFieldStyling({})}
                />
                <IconButton
                  aria-label="visibility"
                  disableRipple
                  sx={{
                    cursor: "pointer",
                    mx: "1.5rem",
                    // pointerEvents: "auto",
                  }}
                  onClick={handleClickShowPassword}
                >
                  <Box component="img" src={showPassword ? images.unlock : images.lock} alt="visibility" />
                </IconButton>
              </Stack>
            </Glass>
          </Stack>

          <Stack
            flexDirection={"row"}
            justifyContent={"space-between"}
            sx={{
              p: "0.5rem",
            }}
          >
            <Typography
              data-testid="email-error"
              sx={{
                fontSize: "1.2rem",
                color: (theme) => theme.palette.brandColor.red,
              }}
            >
              {emailError && typeof emailError === "string" ? t(emailError) : null}
            </Typography>
            <Button
              variant="text"
              disabled={emailError}
              onClick={() => forgotPasswordSubmit(email)}
              sx={{
                p: "0.5rem",
                minWidth: 0,
                pointerEvents: "auto",
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 200,
                color: "#0166FF",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
                "&.Mui-disabled": {
                  color: "gray",
                  textDecoration: "none",
                },
              }}
            >
              {t("FORGOT_KEY")}
            </Button>
          </Stack>
        </Stack>

        <Stack direction={"column"} alignItems={"flex-end"} gap={"1rem"}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
            gap="1rem"
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-disabled={isDisabled}
            sx={{
              width: "38rem!important",
              opacity: isDisabled ? 0.6 : 1,
              cursor: isDisabled ? "not-allowed" : "pointer",
              padding: "1.2rem 2.5rem",
              borderRadius: "3rem",
              backgroundColor: theme.palette.primary.activeBorderColor,
              ...focusVisibleSx(theme),
            }}
            data-testid="login-button"
            onClick={() => {
              if (!isDisabled) submitUser(email, password);
            }}
            onKeyDown={onEnterSpace(() => {
              if (!isDisabled) submitUser(email, password);
            })}
          >
            <Typography
              sx={{
                fontSize: "1.3rem",
                color: "white",
              }}
            >
              {t("loginButton")}
            </Typography>
            <Box component="img" src={images.arrowRight} />
          </Stack>

          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"0.5rem"}
            sx={{
              py: "1.5rem",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.tooltipBackground,
                fontWeight: "400",
                fontSize: "1.2rem",
              }}
            >
              {t("orViaLabel")}
            </Typography>
            <IconButton
              disableRipple
              className="okta-icon"
              aria-label="Sign in with Okta"
              onClick={() => {
                dispatch(
                  loaderStateChange({
                    isLoading: true,
                    isParent: true,
                  })
                );
                onOKTAButtonClick();
              }}
            >
              <Box
                sx={{
                  width: "5.5rem",
                  cursor: "pointer",
                  pointerEvents: "auto",
                }}
                component="img"
                src={images.oktaLight}
                alt="okta"
              />
            </IconButton>
            <Typography
              sx={{
                color: theme.palette.primary.tooltipBackground,
                fontWeight: "400",
                fontSize: "1.2rem",
              }}
            >
              /
            </Typography>
            <IconButton
              disableRipple
              className="okta-icon"
              aria-label="Sign in with Microsoft"
              onClick={() => handleSSO(email)}
            >
              <Box
                sx={{
                  width: "8.5rem",
                  cursor: "pointer",
                  pointerEvents: "auto",
                }}
                component="img"
                src={images.microsoftLight}
                alt="okta"
              />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </Stack>

  );
}

export default LoginForm;
